import React, { useState } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { ALLOWED_NAME_CHARS_REGEX, COVER_CROP_MIX_TYPE, MAX_NAME_LENGTH, MIN_NAME_LENGTH, STALE_VALUE } from "consts";

import { CmcScenarioRecommendation, SoilSightCoverCropTiming } from "models";

import { Button, Card, ContextMenu, ContextMenuItemProps, Icon, InputInline, Tooltip, Typography } from "components";

import { CoverCropCtaButton, CoverCropRecommenderOverlay } from "pages/app/scenario-analysis/components/CoverCropRecommender";
import CropRotationTable from "pages/app/scenario-analysis/components/crop-rotation/CropRotationTable";
import type { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import { formatWholeSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import { percentChange } from "pages/app/scenario-analysis/utils/scenarioComparison";

export interface ScenarioCardProps {
  title: string;
  scenarioId?: string;
  className?: string;
  dataTestId?: string;
  isReference?: boolean;
  isBaseline?: boolean;
  onDelete?: () => void;
  onTitleChange?: (title: string) => void;
  years?: CropYear[];
  /** Null while there is no current run to read from, so the card shows a placeholder. */
  carbonValue?: number | null;
  referenceCarbonValue?: number | null;
  mainCropOptions: ScenarioMainCropOption[];
  /** Display names keyed by crop id (`useGetPracticeCrops` incl. crops not eligible for Silver model). */
  mainCropLabelLookup: Record<string, string>;
  isMainCropOptionsLoading?: boolean;
  onYearDataChange?: (yearId: string, updates: Partial<CropYear>) => void;
  onApplyCoverCropMix?: (yearId: string, updates: Pick<CropYear, "coverCropId" | "legumeShare">) => void;
  onYearRemove?: (yearId: string) => void;
  onYearRefine?: (yearId: string) => void;
  onYearReorder?: (fromIndex: number, toIndex: number) => void;
  onAddYear?: () => void;
  invalidYearIds?: string[];
  tillageErrorYearIds?: string[];
  residueErrorYearIds?: string[];
  addYearErrorMessage?: string;
  validationErrorMessage?: string;
  /** Silver's cover-crop timing for the field's region; null or absent shows no cover-crop warnings. */
  coverCropTiming?: SoilSightCoverCropTiming | null;
}

export const ScenarioCard = ({
  title,
  scenarioId,
  className = "",
  dataTestId = "",
  isReference = false,
  isBaseline = false,
  onDelete,
  onTitleChange,
  years = [],
  carbonValue = null,
  referenceCarbonValue = null,
  onYearDataChange,
  onApplyCoverCropMix,
  onYearRemove,
  onYearRefine,
  onYearReorder,
  onAddYear,
  invalidYearIds = [],
  tillageErrorYearIds = [],
  residueErrorYearIds = [],
  addYearErrorMessage = "",
  validationErrorMessage,
  mainCropOptions,
  mainCropLabelLookup,
  isMainCropOptionsLoading = false,
  coverCropTiming = null,
}: ScenarioCardProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { formatCmOutcome } = useSoilSightCurrency();
  const showCoverCropRecommender = !isReference;
  const [isCoverCropRecommenderOverlayOpen, setOverlayOpen] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editError, setEditError] = useState<string>("");
  const handleApplyRecommendedCoverCropMix = (slot: CmcScenarioRecommendation, legumeShare: number) => {
    const yearId = years[slot.yearIndex - 1]?.id;
    if (yearId) {
      onApplyCoverCropMix?.(yearId, { coverCropId: slot.mixType ?? COVER_CROP_MIX_TYPE.NONE, legumeShare });
    }
  };

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    // Same fallback as displayTitle — pre-fills the input for old projects with no stored name.
    setEditTitle(title || (isReference ? t("scenarioBuilder.reference") : ""));
    setEditError("");
  };

  const getValidationError = (value: string): string | null => {
    const trimmed = value.trim();
    const { length } = trimmed;

    if (value !== value.trimStart()) {
      return t("scenarioBuilder.validation.noLeadingSpaces");
    }
    if (length < MIN_NAME_LENGTH) {
      return t("scenarioBuilder.validation.tooShort");
    }
    if (length > MAX_NAME_LENGTH) {
      return t("scenarioBuilder.validation.tooLong");
    }
    if (!ALLOWED_NAME_CHARS_REGEX.test(trimmed)) {
      return t("scenarioBuilder.validation.invalidCharacters");
    }
    if (!/[a-zA-Z]/.test(trimmed)) {
      return t("scenarioBuilder.validation.noLetter");
    }
    return null;
  };

  const handleTitleChange = (newValue: string) => {
    setEditTitle(newValue);
    setEditError(getValidationError(newValue) ?? "");
  };

  const saveTitle = () => {
    const error = getValidationError(editTitle);

    if (error) {
      setEditError(error);
      return;
    }
    const trimmedTitle = editTitle.trim();
    if (trimmedTitle && trimmedTitle !== title) {
      onTitleChange?.(trimmedTitle);
    }
    setIsEditing(false);
    setEditError("");
  };

  const cancelEdit = () => {
    setEditTitle(title);
    setIsEditing(false);
    setEditError("");
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleRecommendCoverCrops = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOverlayOpen(true);
  };

  // Unrounded, like Key insights, so the two show the same percentage for the same pair.
  const marginChange = carbonValue === null || referenceCarbonValue === null ? null : percentChange(carbonValue, referenceCarbonValue);
  // Always use the locale's reference label for reference scenarios — the stored DB name may be
  // stale ("Scenario 1") from before the referenceName was localised on project creation.
  const displayTitle = isReference && !title ? t("scenarioBuilder.reference") : title || "";
  const marginLabel = carbonValue === null ? STALE_VALUE : t("scenarioComparison.perHectare", { value: formatCmOutcome(carbonValue) });

  const actionItems: ContextMenuItemProps[] = [
    { label: t("scenarioBuilder.rename"), iconName: "tabler/pencil", onClick: startEditing },
    ...(isBaseline ? [] : [{ label: t("scenarioBuilder.delete"), iconName: "tabler/trash", state: "warning" as const, onClick: handleDelete }]),
  ];

  const renderActionsMenu = () => (
    <ContextMenu
      items={actionItems}
      placement="bottom-left"
      visible={isActionsMenuOpen}
      onClose={() => setIsActionsMenuOpen(false)}
      dataTestId={`${dataTestId}-actions`}
      className="scenario-analysis-card-actions-menu"
      iconSize="s"
    >
      <Button
        type="text"
        size="small"
        className="scenario-analysis-card-actions-trigger"
        dataTestId={`${dataTestId}-actions-trigger`}
        aria-label={t("scenarioBuilder.scenarioActions")}
        onClick={() => setIsActionsMenuOpen(true)}
      >
        <Icon iconName="tabler/dots-vertical" size="s" />
      </Button>
    </ContextMenu>
  );

  return (
    <Card
      className={`scenario-analysis-card ${isReference ? "reference" : ""} ${className}`}
      dataTestId={dataTestId}
      {...(scenarioId ? { "data-scenario-id": scenarioId } : {})}
    >
      {/* Header with controls */}
      <div className="scenario-analysis-card-header">
        <div className="header-main">
          {/* Left side - Title section */}
          <div className="header-left">
            {/* Title - becomes editable inline */}
            {isEditing ? (
              <>
                <InputInline
                  value={editTitle}
                  error={editError}
                  maxLength={MAX_NAME_LENGTH + 1}
                  dataTestId={`${dataTestId}-edit-input`}
                  onChange={handleTitleChange}
                  onSave={saveTitle}
                  onCancel={cancelEdit}
                />
                <Icon
                  iconName="tabler/check"
                  size="s"
                  className={`edit-action-icon accept ${editError ? "disabled" : ""}`}
                  dataTestId={`${dataTestId}-edit-accept`}
                  onClick={(e) => {
                    if (!editError) {
                      e.stopPropagation();
                      saveTitle();
                    }
                  }}
                />
                <Icon
                  iconName="tabler/x"
                  size="s"
                  className="edit-action-icon cancel"
                  dataTestId={`${dataTestId}-edit-cancel`}
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelEdit();
                  }}
                />
              </>
            ) : (
              <Tooltip title={displayTitle} placement="topLeft">
                <div className="scenario-analysis-card-title-wrap" data-testid={`${dataTestId}-title`}>
                  <Typography variant="h5" className="scenario-analysis-card-title">
                    {displayTitle}
                  </Typography>
                </div>
              </Tooltip>
            )}

            {/* Rename and delete live in one menu beside the name. */}
            {!isEditing && renderActionsMenu()}
          </div>
        </div>

        {/* Right side - Metrics & controls (€/ha only; CO₂ deferred) */}
        <div className="header-right">
          {/* A dash until a finished run matches the current inputs; the builder passes null while stale. */}
          <div className="summary-metrics">
            <div className="metric">
              <Tooltip title={t("scenarioBuilder.metricTooltip")} placement="top">
                <span className="value">{marginLabel}</span>
              </Tooltip>
            </div>
          </div>

          {/* Percentage Change Indicator - hidden for reference */}
          {!isReference && (
            <div
              className={`change-indicator ${
                marginChange === null ? "neutral is-pending" : marginChange > 0 ? "positive" : marginChange < 0 ? "negative" : "neutral"
              }`}
            >
              {/* Holds the badge's place while stale; a trend icon would claim a direction there is none of. */}
              {marginChange !== null && <Icon iconName={marginChange >= 0 ? "tabler/trending-up" : "tabler/trending-down"} size="s" />}
              <span>{marginChange === null ? STALE_VALUE : formatWholeSignedPercent(marginChange)}</span>
            </div>
          )}

          {/* Always rendered so the controls before it stay aligned on the baseline row, which has no CTA. */}
          <div className="scenario-analysis-cover-crop-slot">{showCoverCropRecommender && <CoverCropCtaButton onClick={handleRecommendCoverCrops} />}</div>
        </div>
      </div>

      <CropRotationTable
        years={years}
        mainCropOptions={mainCropOptions}
        isMainCropOptionsLoading={isMainCropOptionsLoading}
        onYearDataChange={onYearDataChange}
        onYearRemove={onYearRemove}
        onYearRefine={onYearRefine}
        onYearReorder={onYearReorder}
        onAddYear={onAddYear}
        invalidYearIds={invalidYearIds}
        tillageErrorYearIds={tillageErrorYearIds}
        residueErrorYearIds={residueErrorYearIds}
        addYearErrorMessage={addYearErrorMessage}
        validationErrorMessage={validationErrorMessage}
        dataTestId={dataTestId}
        coverCropTiming={coverCropTiming}
      />
      {showCoverCropRecommender && (
        <CoverCropRecommenderOverlay
          open={isCoverCropRecommenderOverlayOpen}
          onClose={() => setOverlayOpen(false)}
          scenarioId={scenarioId}
          mainCropLabelLookup={mainCropLabelLookup ?? {}}
          mainCropIds={years.map((y) => y.crop)}
          onApply={handleApplyRecommendedCoverCropMix}
        />
      )}
    </Card>
  );
};

export default ScenarioCard;
